import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Typography
} from 'antd';
import { EditOutlined, ReloadOutlined, SearchOutlined, UserAddOutlined } from '@ant-design/icons';
import { CompanyUser, CompanyUserCategory, CompanyUserRequest, CompanyUserType } from '../store/user/types';

const { Text, Title } = Typography;

const DEFAULT_ORGANIZATION_ID = '9d779469-6321-4978-8898-7fa114e134fe';
const DEFAULT_ORGANIZATION_NAME = 'CODING THEBLUE';

const typeOptions: { value: CompanyUserType; label: string }[] = [
    { value: 'SOCIO', label: 'Socio' },
    { value: 'TRABAJADOR', label: 'Trabajador' },
    { value: 'PROVEEDOR', label: 'Proveedor' },
    { value: 'CONTRATISTA', label: 'Contratista' },
    { value: 'OTRO', label: 'Otro' }
];

const categoryOptions: { value: CompanyUserCategory; label: string }[] = [
    { value: 'SOCIO', label: 'Socio' },
    { value: 'NOMINA', label: 'Nomina' },
    { value: 'SERVICIOS_PROFESIONALES', label: 'Servicios profesionales' },
    { value: 'EXTERNO', label: 'Externo' },
    { value: 'PROVEEDOR', label: 'Proveedor' }
];

interface PlanBillingPeopleProps {
    getCompanyUsers: (organizationId?: string) => Promise<CompanyUser[]>;
    postCompanyUser: (value: CompanyUserRequest) => Promise<unknown>;
    putCompanyUser: (value: CompanyUserRequest) => Promise<unknown>;
}

const getStatusColor = (status?: string) => {
    if (status === 'ACTIVO') return 'green';
    if (status === 'BLOQUEADO') return 'orange';
    return 'red';
};

const PlanBillingPeople: React.FC<PlanBillingPeopleProps> = ({ getCompanyUsers, postCompanyUser, putCompanyUser }) => {
    const [form] = Form.useForm();
    const [users, setUsers] = useState<CompanyUser[]>([]);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState<string>('TODOS');
    const [categoryFilter, setCategoryFilter] = useState<string>('TODOS');
    const [selectedUser, setSelectedUser] = useState<CompanyUser | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    const loadUsers = async () => {
        const response = await getCompanyUsers(DEFAULT_ORGANIZATION_ID);
        setUsers(response || []);
    };

    useEffect(() => {
        loadUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredUsers = useMemo(() => {
        const value = search.trim().toLowerCase();

        return users.filter(user => {
            const matchesSearch = !value
                || user.dni.toLowerCase().includes(value)
                || user.fullName.toLowerCase().includes(value)
                || String(user.email || '').toLowerCase().includes(value)
                || String(user.phone || '').toLowerCase().includes(value)
                || String(user.observation || '').toLowerCase().includes(value);

            const matchesType = typeFilter === 'TODOS' || user.type === typeFilter;
            const matchesCategory = categoryFilter === 'TODOS' || user.category === categoryFilter;

            return matchesSearch && matchesType && matchesCategory;
        });
    }, [users, search, typeFilter, categoryFilter]);

    const summary = useMemo(() => ({
        socios: users.filter(user => user.type === 'SOCIO').length,
        trabajadores: users.filter(user => user.type === 'TRABAJADOR').length,
        proveedores: users.filter(user => user.type === 'PROVEEDOR' || user.type === 'CONTRATISTA').length,
        activos: users.filter(user => user.status === 'ACTIVO').length
    }), [users]);

    const openCreateModal = () => {
        setSelectedUser(null);
        form.resetFields();
        form.setFieldsValue({
            organizationId: DEFAULT_ORGANIZATION_ID,
            type: 'TRABAJADOR',
            category: 'SERVICIOS_PROFESIONALES',
            status: 'ACTIVO'
        });
        setModalOpen(true);
    };

    const openEditModal = (user: CompanyUser) => {
        setSelectedUser(user);
        form.resetFields();
        form.setFieldsValue({
            organizationId: user.organizationId || DEFAULT_ORGANIZATION_ID,
            dni: user.dni,
            fullName: user.fullName,
            email: user.email,
            phone: user.phone,
            type: user.type,
            category: user.category,
            status: user.status,
            observation: user.observation
        });
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setSelectedUser(null);
        form.resetFields();
    };

    const handleSubmit = async () => {
        const values = await form.validateFields();
        const request: CompanyUserRequest = {
            id: selectedUser?.id || null,
            organizationId: DEFAULT_ORGANIZATION_ID,
            dni: values.dni,
            fullName: values.fullName,
            email: values.email || null,
            phone: values.phone || null,
            type: values.type,
            category: values.category || null,
            status: values.status,
            observation: values.observation || null
        };

        const response = selectedUser
            ? await putCompanyUser(request)
            : await postCompanyUser(request);

        if (response) {
            await loadUsers();
            closeModal();
        }
    };

    const columns = [
        {
            title: 'Persona',
            key: 'person',
            render: (_: unknown, row: CompanyUser) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{row.fullName}</Text>
                    <Text type="secondary">{row.dni}</Text>
                </Space>
            )
        },
        {
            title: 'Contacto',
            key: 'contact',
            render: (_: unknown, row: CompanyUser) => (
                <Space direction="vertical" size={0}>
                    <Text>{row.email || 'Sin email'}</Text>
                    <Text type="secondary">{row.phone || 'Sin telefono'}</Text>
                </Space>
            )
        },
        {
            title: 'Relacion',
            key: 'relation',
            render: (_: unknown, row: CompanyUser) => (
                <Space direction="vertical" size={0}>
                    <Tag color={row.type === 'SOCIO' ? 'blue' : row.type === 'TRABAJADOR' ? 'purple' : 'geekblue'}>{row.type}</Tag>
                    <Text type="secondary">{row.category || 'Sin categoria'}</Text>
                </Space>
            )
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            width: 130,
            render: (value: string) => <Tag color={getStatusColor(value)}>{value}</Tag>
        },
        {
            title: 'Observacion',
            dataIndex: 'observation',
            key: 'observation',
            render: (value?: string | null) => value || <Text type="secondary">Sin observacion</Text>
        },
        {
            title: '',
            key: 'actions',
            width: 120,
            align: 'right' as const,
            render: (_: unknown, row: CompanyUser) => (
                <Button icon={<EditOutlined />} onClick={() => openEditModal(row)}>
                    Editar
                </Button>
            )
        }
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div>
                <Title level={4} style={{ marginBottom: 4 }}>Socios y personas</Title>
                <Text type="secondary">Administra socios, trabajadores y personas vinculadas a gastos o pagos de la empresa.</Text>
            </div>

            <Alert
                type="info"
                showIcon
                message={`Organizacion: ${DEFAULT_ORGANIZATION_NAME}`}
                description="Estas personas no reciben accesos al portal por estar registradas aqui. Solo quedan disponibles para control financiero y operativo."
            />

            <Row gutter={[12, 12]}>
                <Col xs={12} md={6}>
                    <Card><Statistic title="Socios" value={summary.socios} /></Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card><Statistic title="Trabajadores" value={summary.trabajadores} /></Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card><Statistic title="Proveedores/Contratistas" value={summary.proveedores} /></Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card><Statistic title="Activos" value={summary.activos} /></Card>
                </Col>
            </Row>

            <Card>
                <Row gutter={[12, 12]} align="bottom">
                    <Col xs={24} md={8}>
                        <Text strong>Buscar</Text>
                        <Input
                            allowClear
                            prefix={<SearchOutlined />}
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Identificacion, nombre, correo, telefono"
                            style={{ marginTop: 6 }}
                        />
                    </Col>
                    <Col xs={24} md={5}>
                        <Text strong>Tipo</Text>
                        <Select
                            value={typeFilter}
                            onChange={setTypeFilter}
                            options={[{ value: 'TODOS', label: 'Todos' }, ...typeOptions]}
                            style={{ width: '100%', marginTop: 6 }}
                        />
                    </Col>
                    <Col xs={24} md={5}>
                        <Text strong>Categoria</Text>
                        <Select
                            value={categoryFilter}
                            onChange={setCategoryFilter}
                            options={[{ value: 'TODOS', label: 'Todas' }, ...categoryOptions]}
                            style={{ width: '100%', marginTop: 6 }}
                        />
                    </Col>
                    <Col xs={24} md={3}>
                        <Button icon={<ReloadOutlined />} block onClick={loadUsers}>
                            Actualizar
                        </Button>
                    </Col>
                    <Col xs={24} md={3}>
                        <Button icon={<UserAddOutlined />} type="primary" block onClick={openCreateModal}>
                            Nueva persona
                        </Button>
                    </Col>
                </Row>
            </Card>

            <Card>
                <Table
                    rowKey={(row) => row.id || row.dni}
                    columns={columns}
                    dataSource={filteredUsers}
                    pagination={{ pageSize: 8 }}
                    scroll={{ x: 980 }}
                />
            </Card>

            <Modal
                title={selectedUser ? 'Editar persona' : 'Nueva persona'}
                open={modalOpen}
                onCancel={closeModal}
                onOk={handleSubmit}
                okText="Guardar"
                cancelText="Cancelar"
                width={760}
                destroyOnHidden
                forceRender
            >
                <Form form={form} layout="vertical">
                    <Row gutter={12}>
                        <Col xs={24} md={12}>
                            <Form.Item name="dni" label="Identificacion" rules={[{ required: true, message: 'Ingrese la identificacion' }]}>
                                <Input placeholder="1727828447" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="fullName" label="Nombre completo" rules={[{ required: true, message: 'Ingrese el nombre completo' }]}>
                                <Input placeholder="Pablo Santacruz" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="email" label="Correo" rules={[{ type: 'email', message: 'Ingrese un correo valido' }]}>
                                <Input placeholder="persona@correo.com" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="phone" label="Telefono">
                                <Input placeholder="0999999999" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="type" label="Tipo" rules={[{ required: true, message: 'Seleccione el tipo' }]}>
                                <Select options={typeOptions} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="category" label="Categoria">
                                <Select allowClear options={categoryOptions} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="status" label="Estado" rules={[{ required: true, message: 'Seleccione el estado' }]}>
                                <Select
                                    options={[
                                        { value: 'ACTIVO', label: 'ACTIVO' },
                                        { value: 'INACTIVO', label: 'INACTIVO' },
                                        { value: 'BLOQUEADO', label: 'BLOQUEADO' }
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item name="observation" label="Observacion">
                                <Input.TextArea rows={3} placeholder="Ej. Socio 50%, pago mensual pendiente, servicios profesionales..." />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </Space>
    );
};

export default PlanBillingPeople;
